import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ExternalLink, Save, Users, ShoppingCart, Package, CreditCard, DollarSign, RefreshCw, UserX,
  ChevronDown, ChevronRight, FlaskConical, Trash2, Webhook, Zap, Check, X, Loader2, MessageSquare, Inbox,
  Paintbrush, ArrowRight,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useTheme } from '../../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { name: 'Registration Lander', path: '/challenge', desc: 'Traffic entry — hero, VSL, questionnaire CTA' },
  { name: '$27 Upsell (+$47 Bump)', path: '/challenge/kit?reg=preview', desc: 'AI Agency Launch Kit + Niche Vault bump' },
  { name: 'Thank You (Free)', path: '/challenge/thanks', desc: 'Calendar lock, community, homework video' },
  { name: 'Thank You (VIP)', path: '/challenge/vip', desc: 'Split screen — 3 jobs + account activation' },
];

const WEBHOOK_FIELD_GROUPS = [
  { group: 'Event (top level)', fields: [
    ['event', 'challenge_registration | challenge_kit_purchased | challenge_kit_declined | challenge_kit_abandoned | challenge_plan_selected | test'],
  ]},
  { group: 'Registrant (user.*)', fields: [
    ['user.name', 'Jane Doe'],
    ['user.first_name', 'Jane'],
    ['user.last_name', 'Doe'],
    ['user.email', 'jane@example.com'],
    ['user.phone', '+1 (555) 123-4567'],
    ['user.country', 'United States'],
    ['user.country_code', 'US'],
    ['user.source', '1-day-challenge'],
    ['user.event_date', 'Tuesday, August 12'],
    ['user.event_time', '12:00 PM EST'],
    ['user.created_at', '2026-08-01T16:20:00Z'],
  ]},
  { group: 'Tags (user.*)', fields: [
    ['user.tag', 'primary tag for the event'],
    ['user.tags', 'challenge-registrant | challenge-kit-buyer | challenge-kit-abandoned | vip, vip - agency'],
  ]},
  { group: 'Quiz answers (user.*, registration only)', fields: [
    ['user.quiz_q1 / quiz_a1', 'What best describes you right now? → Agency owner scaling up'],
    ['user.quiz_q2 / quiz_a2', 'Have you ever sold AI or automation services? → Not yet — ready to start'],
    ['user.quiz_q3 / quiz_a3', "What's your monthly income goal? → $10K–$20K / month"],
    ['user.answers', 'raw array of {q, a} objects'],
  ]},
  { group: 'Purchase (user.*, purchase & plan events)', fields: [
    ['user.amount', '27 | 74'],
    ['user.bump_purchased', 'true | false'],
    ['user.plan', 'launchpad | starter | agency | pro (plan-selected event)'],
  ]},
];

const PUMBLE_TEMPLATE_FIELDS = [
  ['{{name}}', 'Jane Doe'],
  ['{{first_name}}', 'Jane'],
  ['{{last_name}}', 'Doe'],
  ['{{email}}', 'jane@example.com'],
  ['{{phone}}', '+1 (555) 123-4567'],
  ['{{country}}', 'United States'],
  ['{{event_date}}', 'Tuesday, August 12'],
  ['{{event_time}}', '12:00 PM EST'],
  ['{{quiz_q1}}', 'What best describes you right now?'],
  ['{{quiz_a1}}', 'Agency owner scaling up'],
  ['{{quiz_q2}}', 'Have you ever sold AI or automation services?'],
  ['{{quiz_a2}}', 'Not yet — ready to start'],
  ['{{quiz_q3}}', "What's your monthly income goal with AI agents?"],
  ['{{quiz_a3}}', '$10K–$20K / month'],
  ['{{tag}}', 'challenge-registrant'],
  ['{{source}}', '1-day-challenge'],
  ['{{created_at}}', '2026-08-01T16:20:00Z'],
];

export default function FunnelAdminPage() {
  const { isDarkMode } = useTheme();
  const [tab, setTab] = useState('setup');
  const [config, setConfig] = useState({ event_date: '', event_time: '', event_start_iso: '', community_url: '', abandon_minutes: 30, test_mode: false, vsl_url: '', ghl_webhook_url: '', pumble_webhook_url: '', pumble_registration_template: '' });
  const [regs, setRegs] = useState([]);
  const [stats, setStats] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ kit: 'all', bump: 'all', plan: 'all', event: 'all' });
  const [showFields, setShowFields] = useState(false);
  const [showTemplateFields, setShowTemplateFields] = useState(false);
  const [testing, setTesting] = useState(null); // 'ghl' | 'pumble' | null
  const [testResult, setTestResult] = useState({});

  const tp = isDarkMode ? 'text-white' : 'text-[#141414]';
  const ts = isDarkMode ? 'text-gray-400' : 'text-[#141414]/60';
  const tm = isDarkMode ? 'text-gray-500' : 'text-[#141414]/40';
  const card = isDarkMode ? 'glass-card border border-white/10' : 'bg-white border border-black/[0.08] shadow-sm';
  const divider = isDarkMode ? 'border-white/10' : 'border-black/[0.08]';
  const rowDivider = isDarkMode ? 'border-white/5' : 'border-black/[0.05]';
  const input = isDarkMode
    ? 'bg-white/5 border border-white/10 text-white'
    : 'bg-white border border-black/[0.12] text-[#141414]';
  const ghostBtn = isDarkMode ? 'border-white/10 hover:bg-white/5' : 'border-black/[0.12] hover:bg-black/[0.03]';

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/api/funnel/admin/config`, { headers: authHeaders() }),
        axios.get(`${API}/api/funnel/admin/registrations`, { headers: authHeaders() }),
      ]);
      setConfig(c.data);
      setRegs(r.data.registrations);
      setStats(r.data.stats);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load funnel data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (override) => {
    setSaving(true);
    const payload = override || config;
    try {
      await axios.put(`${API}/api/funnel/admin/config`, payload, { headers: authHeaders() });
      toast.success(override ? `Payments switched to ${payload.test_mode ? 'TEST' : 'LIVE'} mode` : 'Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleTestMode = (value) => {
    const next = { ...config, test_mode: value };
    setConfig(next);
    saveConfig(next);
  };

  const sendTest = async (kind) => {
    const url = (kind === 'ghl' ? config.ghl_webhook_url : config.pumble_webhook_url) || '';
    if (!url.trim()) { toast.error('Paste the webhook URL first'); return; }
    setTesting(kind);
    try {
      const r = await axios.post(`${API}/api/funnel/admin/test-webhook`, { webhook_url: url.trim(), kind, template: kind === 'pumble' ? (config.pumble_registration_template || '') : '' }, { headers: authHeaders() });
      setTestResult({ ...testResult, [kind]: r.data });
      if (r.data.ok) toast.success('Test data sent — check the receiving side');
      else toast.error(`Webhook responded with ${r.data.status_code || r.data.error}`);
    } catch (err) {
      setTestResult({ ...testResult, [kind]: { ok: false, error: err.response?.data?.detail || 'Request failed' } });
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const eventOptions = [...new Set(regs.map((r) => r.event_date || ''))];
  const filtered = regs.filter((r) => {
    if (filters.kit === 'purchased' && !r.kit_purchased) return false;
    if (filters.kit === 'declined' && !r.kit_declined) return false;
    if (filters.kit === 'abandoned' && !(r.abandoned_webhook_fired && !r.kit_purchased)) return false;
    if (filters.kit === 'none' && (r.kit_purchased || r.kit_declined || r.abandoned_webhook_fired)) return false;
    if (filters.bump === 'yes' && !r.bump_purchased) return false;
    if (filters.bump === 'no' && r.bump_purchased) return false;
    if (filters.plan === 'none' && r.plan_chosen) return false;
    if (!['all', 'none'].includes(filters.plan) && r.plan_chosen !== filters.plan) return false;
    if (filters.event !== 'all' && (r.event_date || '') !== filters.event) return false;
    return true;
  });

  const removeReg = async (r) => {
    if (!window.confirm(`Delete ${r.name} (${r.email})? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/api/funnel/admin/registrations/${r.reg_id}`, { headers: authHeaders() });
      toast.success('Registrant deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const STAT_CARDS = [
    { icon: Users, label: 'Registrations', value: stats.total ?? 0 },
    { icon: ShoppingCart, label: 'Kit Buyers ($27)', value: stats.kit_buyers ?? 0 },
    { icon: Package, label: 'Bump Buyers ($47)', value: stats.bump_buyers ?? 0 },
    { icon: CreditCard, label: 'Chose a Plan', value: stats.plan_chosen ?? 0 },
    { icon: UserX, label: 'Abandoned Kit', value: stats.abandoned ?? 0 },
    { icon: DollarSign, label: 'Kit Revenue', value: `$${stats.revenue ?? 0}` },
  ];

  const TestBadge = ({ kind }) => {
    const res = testResult[kind];
    if (!res) return null;
    return res.ok ? (
      <span className="flex items-center gap-1.5 text-xs font-medium text-teal-500 bg-teal-400/10 px-3 py-1.5 rounded-lg" data-testid={`funnel-${kind}-test-success`}>
        <Check size={13} /> Received ({res.status_code})
      </span>
    ) : (
      <span className="flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-400/10 px-3 py-1.5 rounded-lg" data-testid={`funnel-${kind}-test-fail`}>
        <X size={13} /> {res.error || `Failed (${res.status_code})`}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="funnel-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${tp}`}>Challenge Funnel</h1>
            <p className={`text-sm mt-1 ${ts}`}>Preview pages, set the event, wire your webhooks, track registrants.</p>
          </div>
          <button onClick={load} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${ghostBtn} ${tp}`} data-testid="funnel-refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 p-1 rounded-xl border w-fit ${divider} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
          {[{ id: 'setup', label: 'Setup' }, { id: 'webhooks', label: 'Webhooks' }, { id: 'registrants', label: `Registrants (${regs.length})` }].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.id ? (isDarkMode ? 'bg-white/10 text-white' : 'bg-white text-[#141414] shadow-sm') : tm}`} data-testid={`funnel-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'setup' && (
          <div className="space-y-6">
            {/* PAGE EDITOR */}
            <div className={`rounded-xl p-5 ${card}`} data-testid="funnel-editor-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center shrink-0">
                    <Paintbrush size={18} className="text-cyan-500" />
                  </span>
                  <div>
                    <h2 className={`font-bold ${tp}`}>Page Editor</h2>
                    <p className={`text-xs mt-1 max-w-xl ${ts}`}>
                      Design all 4 funnel pages visually — click any element in a live preview to edit its text, size, colors, buttons and VSL video. Add, hide or reorder sections, with desktop &amp; mobile previews.
                    </p>
                  </div>
                </div>
                <Link to="/admin/funnel/editor" className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 text-black text-sm font-bold hover:bg-cyan-400 transition-colors" data-testid="funnel-open-editor-btn">
                  Open Page Editor <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* TEST / LIVE MODE */}
            <div className={`rounded-xl p-5 ${card} ${config.test_mode ? 'ring-2 ring-amber-500/60' : ''}`} data-testid="funnel-mode-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${config.test_mode ? 'bg-amber-500/15' : isDarkMode ? 'bg-white/5' : 'bg-black/[0.04]'}`}>
                    <FlaskConical size={18} className={config.test_mode ? 'text-amber-500' : ts} />
                  </span>
                  <div>
                    <h2 className={`font-bold ${tp}`}>Payments mode: <span className={config.test_mode ? 'text-amber-500' : 'text-green-500'}>{config.test_mode ? 'TEST' : 'LIVE'}</span></h2>
                    <p className={`text-xs mt-1 max-w-xl ${ts}`}>
                      {config.test_mode
                        ? 'Checkouts run on Stripe test mode. Use card 4242 4242 4242 4242, any future expiry, any CVC. Test registrants get a TEST badge and are excluded from stats. Webhooks still fire so you can verify them.'
                        : 'Real cards are charged. Flip to TEST to dial in the full funnel yourself with a fake card.'}
                    </p>
                  </div>
                </div>
                <div className={`flex rounded-full border p-1 ${divider}`} data-testid="funnel-mode-toggle">
                  <button onClick={() => toggleTestMode(false)} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${!config.test_mode ? 'bg-green-500 text-black' : tm}`} data-testid="funnel-mode-live-btn">LIVE</button>
                  <button onClick={() => toggleTestMode(true)} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${config.test_mode ? 'bg-amber-500 text-black' : tm}`} data-testid="funnel-mode-test-btn">TEST</button>
                </div>
              </div>
              {config.test_mode && !config.test_key_configured && (
                <div className="mt-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-xs font-semibold" data-testid="funnel-test-key-warning">
                  STRIPE_TEST_API_KEY is not configured in this environment — test checkouts will fail until you add it (Deployments → Environment Variables). Get your test secret key at dashboard.stripe.com → Developers → API keys (Test mode).
                </div>
              )}
            </div>

            {/* Event config */}
            <div className={`rounded-xl p-6 ${card}`} data-testid="funnel-config-card">
              <h2 className={`font-bold mb-4 ${tp}`}>Event Settings</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>Display date (shown on pages)</label>
                  <input value={config.event_date} onChange={(e) => setConfig({ ...config, event_date: e.target.value })} placeholder="e.g. Tuesday, August 12" className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-date" />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>Display time</label>
                  <input value={config.event_time} onChange={(e) => setConfig({ ...config, event_time: e.target.value })} placeholder="e.g. 12:00 PM EST" className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-time" />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>Event start (for countdown & calendar buttons)</label>
                  <input type="datetime-local" value={config.event_start_iso} onChange={(e) => setConfig({ ...config, event_start_iso: e.target.value })} className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-start" />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>Community URL</label>
                  <input value={config.community_url} onChange={(e) => setConfig({ ...config, community_url: e.target.value })} className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-community" />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>VSL video</label>
                  <div className={`px-3 py-2.5 rounded-lg text-xs ${input} opacity-80`} data-testid="funnel-vsl-moved-note">
                    The VSL is now edited in the <Link to="/admin/funnel/editor" className="font-bold text-cyan-500 hover:underline">Page Editor</Link> — click the video in the hero to swap it.
                  </div>
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>Abandoned-cart timer (minutes)</label>
                  <input type="number" min={5} value={config.abandon_minutes} onChange={(e) => setConfig({ ...config, abandon_minutes: parseInt(e.target.value || 30, 10) })} className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-abandon" />
                  <p className={`text-[11px] mt-1 ${tm}`}>Registrants who don't buy the kit within this window (or click "No thanks") get pushed with tag <span className="font-mono">challenge-kit-abandoned</span>. Buyers get <span className="font-mono">challenge-kit-buyer</span>.</p>
                </div>
              </div>
              <button onClick={() => saveConfig()} disabled={saving} className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-500 text-black text-sm font-bold hover:bg-teal-400 disabled:opacity-50 transition-colors" data-testid="funnel-config-save">
                <Save size={14} /> {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>

            {/* Page previews */}
            <div>
              <h2 className={`font-bold mb-3 ${tp}`}>Funnel Pages</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {PAGES.map((p) => (
                  <a key={p.path} href={p.path} target="_blank" rel="noreferrer" className={`rounded-xl p-5 transition-colors group ${card} ${isDarkMode ? 'hover:border-white/25' : 'hover:border-black/25'}`} data-testid={`funnel-page-${p.path.split('?')[0].split('/').pop() || 'lander'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-bold text-sm ${tp}`}>{p.name}</span>
                      <ExternalLink size={14} className={tm} />
                    </div>
                    <div className={`text-xs ${ts}`}>{p.desc}</div>
                    <div className="text-xs text-teal-500 mt-2 font-mono">{p.path}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'webhooks' && (
          <div className="space-y-6">
            <div className={`rounded-xl p-6 ${card}`} data-testid="funnel-webhooks-card">
              <div className="flex items-center gap-2 mb-1">
                <Webhook size={16} className="text-teal-400" />
                <h2 className={`font-bold ${tp}`}>Funnel Webhooks</h2>
              </div>
              <p className={`text-xs mb-5 ${ts}`}>Every funnel event (registration, kit purchase, decline, abandon, VIP plan) is POSTed here in real time.</p>

              {/* GHL */}
              <label className={`block text-xs font-semibold mb-1.5 ${ts}`}>GHL Inbound Webhook URL</label>
              <input value={config.ghl_webhook_url || ''} onChange={(e) => { setConfig({ ...config, ghl_webhook_url: e.target.value }); setTestResult({ ...testResult, ghl: null }); }} placeholder="https://services.leadconnectorhq.com/hooks/…" className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-ghl-webhook" />
              <p className={`text-[11px] mt-1.5 ${tm}`}>In GHL: Workflows → Trigger "Inbound Webhook" → copy the URL here. If left empty, funnel events fall back to your global signup webhook. Hit Save after pasting.</p>
              <div className="flex flex-wrap items-center gap-2.5 mt-3">
                <button onClick={() => sendTest('ghl')} disabled={testing === 'ghl' || !(config.ghl_webhook_url || '').trim()} className="px-4 py-2 rounded-lg bg-sky-400/15 text-sky-500 border border-sky-400/30 text-xs font-semibold hover:bg-sky-400/25 transition-all disabled:opacity-50 flex items-center gap-1.5" data-testid="funnel-send-test-ghl">
                  {testing === 'ghl' ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                  {testing === 'ghl' ? 'Sending…' : 'Send Test Data'}
                </button>
                <TestBadge kind="ghl" />
              </div>

              {/* field reference */}
              <button onClick={() => setShowFields((v) => !v)} className={`mt-4 flex items-center gap-1.5 text-xs font-medium ${ts} hover:text-teal-400 transition-colors`} data-testid="funnel-toggle-webhook-fields">
                <ChevronDown size={14} className={`transition-transform ${showFields ? 'rotate-180' : ''}`} />
                {showFields ? 'Hide' : 'View'} the {WEBHOOK_FIELD_GROUPS.reduce((n, g) => n + g.fields.length, 0)} data fields that will be sent
              </button>
              {showFields && (
                <div className={`mt-3 rounded-xl border ${divider} overflow-hidden`} data-testid="funnel-webhook-fields-panel">
                  {WEBHOOK_FIELD_GROUPS.map((g) => (
                    <div key={g.group}>
                      <p className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider ${ts} ${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-100'}`}>{g.group}</p>
                      {g.fields.map(([key, example]) => (
                        <div key={key} className={`flex items-start gap-3 px-4 py-2 border-t ${divider}`}>
                          <code className="text-[11px] text-teal-500 font-mono shrink-0 w-48 truncate">{key}</code>
                          <span className={`text-[11px] ${ts}`}>{example}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Pumble */}
              <div className={`mt-6 pt-5 border-t ${divider}`}>
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare size={14} className="text-violet-400" />
                  <h3 className={`font-semibold text-sm ${tp}`}>Pumble Notifications</h3>
                </div>
                <label className={`block text-xs font-semibold mb-1.5 mt-2 ${ts}`}>Pumble Incoming Webhook URL</label>
                <input value={config.pumble_webhook_url || ''} onChange={(e) => { setConfig({ ...config, pumble_webhook_url: e.target.value }); setTestResult({ ...testResult, pumble: null }); }} placeholder="https://api.pumble.com/workspaces/…/incomingWebhooks/postMessage/…" className={`w-full px-3 py-2.5 rounded-lg text-sm ${input}`} data-testid="funnel-config-pumble-webhook" />
                <p className={`text-[11px] mt-1.5 ${tm}`}>In Pumble: channel → Integrations → Incoming Webhooks → create → paste here. Hit Save after pasting.</p>

                {/* Registrant message template */}
                <div className="flex items-center justify-between mt-4 mb-1.5">
                  <label className={`block text-xs font-semibold ${ts}`}>New Registrant Message Template</label>
                  <button
                    onClick={() => setConfig({ ...config, pumble_registration_template: config.pumble_template_default || '' })}
                    className={`text-[11px] font-semibold ${ts} hover:text-violet-400 transition-colors underline underline-offset-2`}
                    data-testid="funnel-pumble-template-reset"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={config.pumble_registration_template ?? ''}
                  onChange={(e) => setConfig({ ...config, pumble_registration_template: e.target.value })}
                  placeholder={config.pumble_template_default || ''}
                  rows={8}
                  className={`w-full px-3 py-2.5 rounded-lg text-sm font-mono leading-relaxed ${input} resize-y`}
                  data-testid="funnel-pumble-template"
                />
                <p className={`text-[11px] mt-1.5 ${tm}`}>Customize the message posted to Pumble when someone registers. Use <code className="text-violet-400">{'{{field}}'}</code> placeholders — empty fields render as "—". Other funnel events (kit purchase, decline, abandon, VIP plan) keep their standard one-line messages. Leave blank to use the default.</p>

                <button onClick={() => setShowTemplateFields((v) => !v)} className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${ts} hover:text-violet-400 transition-colors`} data-testid="funnel-toggle-template-fields">
                  <ChevronDown size={14} className={`transition-transform ${showTemplateFields ? 'rotate-180' : ''}`} />
                  {showTemplateFields ? 'Hide' : 'View'} the {PUMBLE_TEMPLATE_FIELDS.length} data fields you can use
                </button>
                {showTemplateFields && (
                  <div className={`mt-3 rounded-xl border ${divider} overflow-hidden`} data-testid="funnel-pumble-template-fields-panel">
                    {PUMBLE_TEMPLATE_FIELDS.map(([key, example]) => (
                      <div key={key} className={`flex items-start gap-3 px-4 py-2 border-t first:border-t-0 ${divider}`}>
                        <code className="text-[11px] text-violet-400 font-mono shrink-0 w-40 truncate">{key}</code>
                        <span className={`text-[11px] ${ts}`}>{example}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2.5 mt-4">
                  <button onClick={() => sendTest('pumble')} disabled={testing === 'pumble' || !(config.pumble_webhook_url || '').trim()} className="px-4 py-2 rounded-lg bg-violet-400/15 text-violet-500 border border-violet-400/30 text-xs font-semibold hover:bg-violet-400/25 transition-all disabled:opacity-50 flex items-center gap-1.5" data-testid="funnel-send-test-pumble">
                    {testing === 'pumble' ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                    {testing === 'pumble' ? 'Sending…' : 'Send Test Message'}
                  </button>
                  <TestBadge kind="pumble" />
                </div>
              </div>

              <button onClick={() => saveConfig()} disabled={saving} className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-500 text-black text-sm font-bold hover:bg-teal-400 disabled:opacity-50 transition-colors" data-testid="funnel-webhooks-save">
                <Save size={14} /> {saving ? 'Saving…' : 'Save webhooks'}
              </button>
            </div>
          </div>
        )}

        {tab === 'registrants' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {STAT_CARDS.map((s) => (
                <div key={s.label} className={`rounded-xl p-4 ${card}`} data-testid={`funnel-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <s.icon size={16} className={`${ts} mb-2`} />
                  <div className={`text-2xl font-bold ${tp}`}>{s.value}</div>
                  <div className={`text-xs mt-0.5 ${ts}`}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Registrants table */}
            <div className={`rounded-xl overflow-hidden ${card}`} data-testid="funnel-registrants-table">
              <div className={`px-5 py-4 border-b flex flex-wrap items-center gap-3 ${divider}`}>
                <span className={`font-bold ${tp}`}>Registrants ({filtered.length}{filtered.length !== regs.length ? ` of ${regs.length}` : ''})</span>
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                  <select value={filters.kit} onChange={(e) => setFilters({ ...filters, kit: e.target.value })} className={`px-2.5 py-1.5 rounded-lg text-xs ${input}`} data-testid="funnel-filter-kit">
                    <option value="all">Kit: all</option>
                    <option value="purchased">Kit: purchased</option>
                    <option value="declined">Kit: declined</option>
                    <option value="abandoned">Kit: abandoned</option>
                    <option value="none">Kit: no action</option>
                  </select>
                  <select value={filters.bump} onChange={(e) => setFilters({ ...filters, bump: e.target.value })} className={`px-2.5 py-1.5 rounded-lg text-xs ${input}`} data-testid="funnel-filter-bump">
                    <option value="all">Bump: all</option>
                    <option value="yes">Bump: yes</option>
                    <option value="no">Bump: no</option>
                  </select>
                  <select value={filters.plan} onChange={(e) => setFilters({ ...filters, plan: e.target.value })} className={`px-2.5 py-1.5 rounded-lg text-xs ${input}`} data-testid="funnel-filter-plan">
                    <option value="all">Plan: all</option>
                    <option value="launchpad">Launchpad</option>
                    <option value="starter">Starter</option>
                    <option value="agency">Agency</option>
                    <option value="pro">Pro</option>
                    <option value="none">No plan</option>
                  </select>
                  <select value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })} className={`px-2.5 py-1.5 rounded-lg text-xs ${input}`} data-testid="funnel-filter-event">
                    <option value="all">Event: all</option>
                    {eventOptions.map((ev) => (
                      <option key={ev || '_none'} value={ev}>{ev || '(no event set)'}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left text-xs ${ts}`}>
                      <th className="px-3 py-3 w-8"></th>
                      <th className="px-2 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Event</th>
                      <th className="px-4 py-3">Kit</th>
                      <th className="px-4 py-3">Bump</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3">Registered</th>
                      <th className="px-3 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={10} className={`px-5 py-12 text-center ${tm}`}>
                          <Inbox size={32} className="mx-auto mb-2 opacity-40" />
                          {regs.length === 0 ? 'No registrations yet — share /challenge to start filling seats.' : 'No registrants match these filters.'}
                        </td>
                      </tr>
                    )}
                    {filtered.map((r) => (
                      <React.Fragment key={r.reg_id}>
                        <tr className={`border-t ${rowDivider}`}>
                          <td className="px-3 py-3">
                            <button onClick={() => setExpanded(expanded === r.reg_id ? null : r.reg_id)} className={tm} data-testid={`funnel-reg-expand-${r.reg_id}`}>
                              {expanded === r.reg_id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            </button>
                          </td>
                          <td className={`px-2 py-3 font-medium ${tp}`}>
                            {r.name}
                            {r.is_test && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500 text-[10px] font-bold align-middle">TEST</span>}
                          </td>
                          <td className={`px-4 py-3 ${ts}`}>{r.email}</td>
                          <td className={`px-4 py-3 ${ts}`}>{r.phone}</td>
                          <td className={`px-4 py-3 text-xs ${ts}`}>{r.event_date || <span className={tm}>—</span>}</td>
                          <td className="px-4 py-3">{r.kit_purchased ? <span className="text-green-500 font-bold">$27 ✓</span> : r.kit_declined ? <span className="text-amber-500 text-xs font-bold">declined</span> : r.abandoned_webhook_fired ? <span className="text-amber-500 text-xs font-bold">abandoned</span> : <span className={tm}>—</span>}</td>
                          <td className="px-4 py-3">{r.bump_purchased ? <span className="text-amber-500 font-bold">$47 ✓</span> : <span className={tm}>—</span>}</td>
                          <td className="px-4 py-3">{r.plan_chosen ? <span className="px-2 py-1 rounded-full bg-teal-500/15 text-teal-600 text-xs font-bold capitalize">{r.plan_chosen}</span> : <span className={tm}>no plan</span>}</td>
                          <td className={`px-4 py-3 text-xs ${tm}`}>{(r.created_at || '').slice(0, 10)}</td>
                          <td className="px-3 py-3">
                            <button onClick={() => removeReg(r)} className={`${tm} hover:text-red-500 transition-colors`} title="Delete registrant" data-testid={`funnel-reg-delete-${r.reg_id}`}>
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                        {expanded === r.reg_id && (
                          <tr className={`border-t ${rowDivider}`}>
                            <td colSpan={10} className={`px-12 py-4 ${isDarkMode ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}`} data-testid={`funnel-reg-answers-${r.reg_id}`}>
                              {(r.answers || []).length === 0 ? (
                                <span className={`text-xs ${tm}`}>No questionnaire answers recorded.</span>
                              ) : (
                                <div className="space-y-2">
                                  {r.answers.map((qa, i) => (
                                    <div key={i} className="text-xs">
                                      <span className={ts}>{qa.q}</span>{' '}
                                      <span className={`font-bold ${tp}`}>→ {qa.a || '—'}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
